import { useTranslation } from "react-i18next";
import { Box, Chip, Skeleton, Typography, Divider } from "@mui/material";

import { Service } from "../../../../../types/service.type";
import {
  IdealCompletionTimeIcon,
  NumberOfVideosIcon,
  ProgramLevelIcon,
} from "../../../../../assets/icons/service-details-icons";

import ProgramReviews from "./ProgramReviews";
import ProgramEquipments from "./ProgramEquipments";

const ProgramDetail = ({
  icon: Icon,
  label,
  value,
  isLoading,
}: {
  icon: React.FC;
  label: string;
  value: string;
  isLoading?: boolean;
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", gap: "16px" }}>
        <Skeleton variant="circular" width={24} height={24} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Skeleton variant="text" width={80} height={20} />
          <Skeleton variant="text" width={120} height={20} />
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", gap: "16px" }}>
      <Icon />
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <Typography sx={styles.detailLabel}>{label}</Typography>
        <Typography sx={styles.detailValue}>{value}</Typography>
      </Box>
    </Box>
  );
};

const ProgramBanner = ({ banner, isLoading }: { banner?: { url: string }; isLoading?: boolean }) => {
  if (isLoading) return <Skeleton variant="rectangular" width="100%" height={260} />;

  return (
    <Box
      sx={{
        ...styles.banner,
        backgroundImage: banner ? `url(${banner.url})` : "none",
        backgroundColor: "#F5F5F5",
      }}
    />
  );
};

const DescriptionSection = ({ description, isLoading }: { description?: string; isLoading?: boolean }) => {
  const { t } = useTranslation("goService");

  if (isLoading)
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Skeleton variant="text" width={120} height={28} />
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="90%" height={20} />
        <Skeleton variant="text" width="95%" height={20} />
      </Box>
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Typography sx={styles.sectionTitle}>{t("programRequestPage.description")}</Typography>
      <Typography sx={styles.description}>{description}</Typography>
    </Box>
  );
};

const CategorySection = ({
  category,
  subCategory,
  isLoading,
}: {
  category?: string;
  subCategory?: string;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation("goService");

  if (isLoading)
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Skeleton variant="text" width={100} height={28} />
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Skeleton variant="rounded" width={120} height={28} />
          <Skeleton variant="rounded" width={140} height={28} />
        </Box>
      </Box>
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Typography sx={styles.sectionTitle}>{t("programRequestPage.category")}</Typography>
      <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {category && <Chip sx={styles.categoryChip} label={t(`serviceCategory.${category}`)} />}
        {subCategory && <Chip sx={styles.categoryChip} label={t(`serviceSubCategory.${subCategory}`)} />}
      </Box>
    </Box>
  );
};

const AboutTab = ({ programService }: { programService?: Service }) => {
  const { t } = useTranslation("goService");

  if (!programService) return null;
  const idealCompletionTime = programService?.idealCompletionTime;
  const numberOfVideos = programService?.numberOfVideos;
  const programLevel = programService?.level;

  return (
    <Box sx={styles.container}>
      <ProgramBanner banner={programService?.banner} />
      <Box sx={styles.contentWrapper}>
        <DescriptionSection description={programService?.description} />
        <CategorySection category={programService?.category} subCategory={programService?.subCategory} />

        <Box sx={styles.detailsContainer}>
          {idealCompletionTime && (
            <ProgramDetail
              icon={IdealCompletionTimeIcon}
              label={t("programRequestPage.idealProgramFinishesIn")}
              value={t("programRequestPage.month", { count: idealCompletionTime })}
            />
          )}

          {idealCompletionTime && numberOfVideos && <Divider />}

          {numberOfVideos && (
            <ProgramDetail
              icon={NumberOfVideosIcon}
              label={t("programRequestPage.numberOfVideos")}
              value={t("programRequestPage.video", { count: numberOfVideos })}
            />
          )}

          {programLevel && numberOfVideos && <Divider />}

          {programLevel && (
            <ProgramDetail
              icon={ProgramLevelIcon}
              label={t("programRequestPage.programLevel")}
              value={t(`programLevel.${programLevel}`)}
            />
          )}
        </Box>
        <ProgramEquipments programService={programService} />
        <Divider />
        <ProgramReviews programService={programService} />
      </Box>
    </Box>
  );
};

export default AboutTab;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    borderRadius: "8px",
    overflow: "hidden",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
  },
  banner: {
    width: "100%",
    height: "260px",
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "0 16px 24px",
  },
  sectionTitle: {
    color: "var(--Gray-900, #1D2026)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  description: {
    fontFamily: "Montserrat",
    color: "var(--Gray-700, #4E5566)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "150%",
  },
  categoryChip: {
    color: "var(--Green---100, #008B07)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    padding: "4px 12px",
    borderRadius: "16px",
    backgroundColor: "var(--Green---25, #E7EFED)",
    "& .MuiChip-label": {
      padding: "0",
    },
  },
  detailsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
    padding: "16px",
  },
  detailLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  detailValue: {
    fontFamily: "Montserrat",
    color: "var(--black-100, #1C1C1C)",
    fontSize: "13px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "16px",
  },
} as const;
